import * as React from "react";
import { createPortal } from "react-dom";

const WIDTH = 256;
const GAP = 6;

/**
 * A short explanation shown on hover (and keyboard focus) under a tab,
 * badge or button. Rendered into <body> at a fixed position, so a
 * scrolling or overflow-hidden container (e.g. a short table) never clips
 * it; it opens above the element when there is no room below and stays
 * inside the window horizontally. `delayMs` holds it back (used for the
 * list tabs, where an instant tooltip gets in the way).
 */
export function HoverHint({
  hint,
  children,
  align = "left",
  delayMs = 0,
}: {
  hint: string;
  children: React.ReactNode;
  align?: "left" | "right";
  delayMs?: number;
}) {
  const id = React.useId();
  const anchor = React.useRef<HTMLSpanElement>(null);
  const timer = React.useRef<number | undefined>(undefined);
  const [pos, setPos] = React.useState<{ top: number; left: number; above: boolean } | null>(null);

  const place = React.useCallback(() => {
    const el = anchor.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const roomBelow = window.innerHeight - r.bottom;
    const above = roomBelow < 120 && r.top > roomBelow;
    const rawLeft = align === "right" ? r.right - WIDTH : r.left;
    const left = Math.max(8, Math.min(rawLeft, window.innerWidth - WIDTH - 8));
    setPos({ top: above ? r.top - GAP : r.bottom + GAP, left, above });
  }, [align]);

  const show = () => {
    window.clearTimeout(timer.current);
    if (delayMs > 0) timer.current = window.setTimeout(place, delayMs);
    else place();
  };
  const hide = () => {
    window.clearTimeout(timer.current);
    setPos(null);
  };
  React.useEffect(() => () => window.clearTimeout(timer.current), []);
  React.useEffect(() => {
    if (!pos) return;
    window.addEventListener("scroll", hide, true);
    window.addEventListener("resize", hide);
    return () => {
      window.removeEventListener("scroll", hide, true);
      window.removeEventListener("resize", hide);
    };
  }, [pos]);

  return (
    <span
      ref={anchor}
      className="relative inline-flex"
      aria-describedby={pos ? id : undefined}
      onMouseEnter={show}
      onMouseLeave={hide}
      onFocus={show}
      onBlur={hide}
    >
      {children}
      {pos
        ? createPortal(
            <span
              id={id}
              role="tooltip"
              style={{ position: "fixed", top: pos.top, left: pos.left, width: WIDTH, transform: pos.above ? "translateY(-100%)" : undefined }}
              className="pointer-events-none z-[1000] rounded-md bg-zinc-900 px-2.5 py-1.5 text-left text-[0.6875rem] font-normal normal-case leading-snug tracking-normal text-white shadow-lg"
            >
              {hint}
            </span>,
            document.body,
          )
        : null}
    </span>
  );
}
